/**
 * Actual road-following route path via Google's Routes API (ROUTE-REAL-02).
 * Given a tech's ordered stops, returns the real drive PATH (a decoded polyline
 * that follows streets) to draw on the map instead of straight pin-to-pin lines.
 *
 * Cost discipline (same playbook as driveTimes.ts): ONE computeRoutes request
 * per distinct route order, cached for the session; sticky-off if the API isn't
 * enabled (403) so we never hammer a disabled endpoint. Callers fall back to the
 * straight-line polylines whenever this returns null.
 *
 * Requires the "Routes API" to be enabled for the Maps key in Google Cloud.
 */
import { useEffect, useState } from "react";
import type { LatLng } from "./route";

const cache = new Map<string, LatLng[] | null>();
let apiUnavailable = false;

/** Standard Google encoded-polyline decoder (1e5 precision). */
function decodePolyline(encoded: string): LatLng[] {
  const points: LatLng[] = [];
  let index = 0;
  let lat = 0;
  let lng = 0;
  while (index < encoded.length) {
    let result = 0;
    let shift = 0;
    let b: number;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    lat += result & 1 ? ~(result >> 1) : result >> 1;
    result = 0;
    shift = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    lng += result & 1 ? ~(result >> 1) : result >> 1;
    points.push({ lat: lat / 1e5, lng: lng / 1e5 });
  }
  return points;
}

function sigOf(ordered: LatLng[]): string {
  return ordered
    .map((p) => `${p.lat.toFixed(5)},${p.lng.toFixed(5)}`)
    .join("|");
}

/** Road path for an ORDERED route. null = API unavailable / not enabled. */
export async function getRoadPath(ordered: LatLng[]): Promise<LatLng[] | null> {
  if (ordered.length < 2) return null;
  const sig = sigOf(ordered);
  if (cache.has(sig)) return cache.get(sig) ?? null;
  if (apiUnavailable) return null;
  const key = import.meta.env.VITE_GOOGLE_MAPS_KEY as string | undefined;
  if (!key) return null;

  const toWp = (p: LatLng) => ({
    location: { latLng: { latitude: p.lat, longitude: p.lng } },
  });
  try {
    const res = await fetch(
      "https://routes.googleapis.com/directions/v2:computeRoutes",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Goog-Api-Key": key,
          "X-Goog-FieldMask": "routes.polyline.encodedPolyline",
        },
        body: JSON.stringify({
          origin: toWp(ordered[0]),
          destination: toWp(ordered[ordered.length - 1]),
          intermediates: ordered.slice(1, -1).map(toWp),
          travelMode: "DRIVE",
        }),
      },
    );
    if (!res.ok) {
      if (res.status === 403) apiUnavailable = true; // API not enabled for key
      cache.set(sig, null);
      return null;
    }
    const data = await res.json();
    const encoded: string | undefined =
      data?.routes?.[0]?.polyline?.encodedPolyline;
    const path = encoded ? decodePolyline(encoded) : null;
    cache.set(sig, path);
    return path;
  } catch {
    return null;
  }
}

/** Hook: the road path for an ordered route, refetched when the order changes. */
export function useRoadPath(ordered: LatLng[]): {
  data: LatLng[] | null;
  loading: boolean;
} {
  const sig = sigOf(ordered);
  const [state, setState] = useState<{
    data: LatLng[] | null;
    loading: boolean;
  }>({ data: null, loading: false });

  useEffect(() => {
    let cancelled = false;
    if (ordered.length < 2) {
      setState({ data: null, loading: false });
      return;
    }
    setState((s) => ({ ...s, loading: true }));
    getRoadPath(ordered).then((p) => {
      if (!cancelled) setState({ data: p, loading: false });
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  return state;
}
